import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useHttpConfig, useUpdateHttpConfig } from "../../hooks/useHttp";
import { HttpRequestConfigUpdate } from "../../types/http";
import AuditInfo from "../../components/ui/AuditInfo";
import { Input, Textarea } from "../../components/ui/Input";
import { auditApi } from "../../services/api";

function toJsonString(value: any): string {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch {
    return "{}";
  }
}
function safeJson(text?: string) {
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

export default function EditHttpConfigPage() {
  const { id } = useParams<{ id: string }>();
  const configId = Number(id);
  const navigate = useNavigate();
  const { data: cfg, isLoading } = useHttpConfig(configId);
  const updateMutation = useUpdateHttpConfig();
  const [form, setForm] = useState<{
    [K in keyof HttpRequestConfigUpdate]?: any;
  }>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (cfg) {
      setForm({
        name: cfg.name,
        base_url: cfg.base_url,
        description: cfg.description || "",
        headers: toJsonString(cfg.headers ?? {}),
        auth_type: cfg.auth_type || "",
        auth_credentials: toJsonString(cfg.auth_credentials ?? {}),
        timeout_seconds: cfg.timeout_seconds ?? 30,
        retry_count: cfg.retry_count ?? 3,
      });
    }
  }, [cfg]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateMutation.mutateAsync({
        id: configId,
        data: {
          name: form.name || undefined,
          base_url: form.base_url || undefined,
          description: form.description || undefined,
          headers: safeJson(form.headers),
          auth_type: form.auth_type || undefined,
          auth_credentials: safeJson(form.auth_credentials),
          timeout_seconds: form.timeout_seconds ?? undefined,
          retry_count: form.retry_count ?? undefined,
        },
      });
      navigate("/http-configs");
    } finally {
      setSaving(false);
    }
  };

  if (isLoading || !cfg) {
    return <div className="py-12 text-center">Loading...</div>;
  }

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          Edit HTTP Config: {cfg.name}
        </h2>
        <p className="text-sm text-gray-500">Update base configuration</p>
      </div>
      <form
        className="bg-white shadow sm:rounded-lg p-6 space-y-4"
        onSubmit={handleSubmit}
      >
        <div>
          <Input
            label="Name"
            type="text"
            value={form.name || ""}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, name: e.target.value }))
            }
            variant="rounded-md"
            required
          />
        </div>
        <div>
          <Input
            label="Base URL"
            type="url"
            value={form.base_url || ""}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, base_url: e.target.value }))
            }
            variant="rounded-md"
            required
          />
        </div>
        <div>
          <Textarea
            label="Description"
            value={form.description || ""}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, description: e.target.value }))
            }
            variant="rounded-md"
            rows={3}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Textarea
              label="Headers (JSON)"
              value={form.headers || ""}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, headers: e.target.value }))
              }
              variant="rounded-md"
              className="font-mono"
              rows={4}
            />
          </div>
          <div>
            <Textarea
              label="Auth Credentials (JSON)"
              value={form.auth_credentials || ""}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  auth_credentials: e.target.value,
                }))
              }
              variant="rounded-md"
              className="font-mono"
              rows={4}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Input
              label="Auth Type"
              type="text"
              value={form.auth_type || ""}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, auth_type: e.target.value }))
              }
              variant="rounded-md"
              placeholder="basic, bearer"
            />
          </div>
          <div>
            <Input
              label="Timeout (seconds)"
              type="number"
              min={0}
              value={form.timeout_seconds ?? 30}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  timeout_seconds: e.target.value
                    ? Number(e.target.value)
                    : undefined,
                }))
              }
              variant="rounded-md"
            />
          </div>
        </div>
        <div>
          <Input
            label="Retry Count"
            type="number"
            min={0}
            value={form.retry_count ?? 3}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                retry_count: e.target.value
                  ? Number(e.target.value)
                  : undefined,
              }))
            }
            variant="rounded-md"
          />
        </div>
        <div className="flex justify-end space-x-2">
          <button
            type="button"
            className="btn-secondary px-4 py-2 rounded-md"
            onClick={() => navigate("/http-configs")}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary px-4 py-2 rounded-md"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
      {cfg?.id ? (
        <AuditInfo
          resource="http-configs"
          id={cfg.id}
          fetch={auditApi.getLastChange}
        />
      ) : null}
    </div>
  );
}
